import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';

import { HoverTime, useFrameTime } from '@/components/ui/hover-time';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { LayoutDetailPhoto } from '@/api/analytics';
import { cn } from '@/lib/utils';
import { OrigCropView, type OrigGeom } from './OrigCropView';
import { PolygonCrop } from '@/components/projection/PolygonCrop';
import { spansDays } from './timeAxis';

// ─── LAYOUT REQUIREMENTS (owner, 2026-07-14) ────────────────────────────────
// The contract this strip MUST meet. The recurring "white band ABOVE the photos"
// is a REGRESSION against these (introduced by bottom-anchoring short photos in a
// fixed-height cell — d4bc8f68 flipped items-center→items-end), NOT a new design
// to invent. When touching vertical sizing, re-check every line here:
//   • Desktop CARD: STABLE layout, NO scroll. Photos an ADEQUATE size. Fewer
//     photos is fine — the row TILES the available width; the count varies.
//   • Mobile CARD: horizontal SCROLL; ALL photos in one row. Photo WIDTH does
//     not matter (you scroll to see them). NEVER crop a photo.
//   • Lightbox (desktop AND mobile): use as much SCREEN HEIGHT as possible to
//     show photos BIG — the container may grow taller/shorter to do so; the
//     photo count varies to tile the space. NEVER crop.
//   • No white band above/below — photos FILL their allotted height (they're
//     sized by HEIGHT; width follows from aspect, and varies per photo).
// ────────────────────────────────────────────────────────────────────────────
//
// Shared photo strip for the День Выкладка / Очередь / Столы chart cards
// (all viewports, card AND fullscreen). Two independent layers share one
// box:
//   - a FIXED layer (never scrolls) holding the timeline rail + one dot per
//     photo at its TRUE time-x against the paired chart's real, unwidened
//     plot width — this is what stays visually locked to the actual chart
//     below, exactly like an axis;
//   - a horizontally SCROLLABLE layer holding the photo thumbnails, packed
//     at a natural (uncompressed) size on a canvas that widens to fit every
//     photo instead of cropping the tail — swipe reveals the rest.
// A thin connector links each dot to its photo; since the two layers move
// independently, the connector is redrawn on scroll (see `scrollLeft`) and
// its elbow height is staggered in a repeating "ladder" rhythm so several
// connectors fanning out from nearby dots to far-apart (scrolled) photos
// don't all cross at one height. Timestamps sit in the photo corner; the
// metric chip (fill% / people) sits opposite.

export interface DayStripPhoto {
  image_url: string | null;
  /** Полный кадр (если сохранился) — для «в оригинальных пропорциях». */
  frameUrl?: string | null;
  /** Кадра нет — фон рамки из ближайшего кадра камеры, поверх свой кроп (столы, #2789). */
  bgFrameUrl?: string | null;
  at: string; // ISO (tz-aware)
  /** Bottom-right metric chip text, already formatted (e.g. "63%" or "5 чел"). */
  metric?: string | null;
  /** Highlight this frame (e.g. a biggest-dip moment) with an orange marker. */
  dip?: boolean;
  /** Incident id — photos of one incident share a connected orange underline
   *  (per lane; e.g. the start/end pair of a dirty-table stretch). */
  group?: string | number;
  /** Raw numeric metric (rack fill %, 0-100) used by the coverageFirst
   *  representative-segment down-selection to detect low-fill problem runs.
   *  Undefined for strips without a fill metric (e.g. the queue). */
  value?: number;
  /** Сколько длился отрезок под фото, с — для подписи в лайтбоксе (столы: «не убран 5 мин»). */
  durationS?: number | null;
}

/** Map layout-detail rack photos to the strip shape, marking INCIDENT frames
 *  as "dips" (orange marker): every genuinely low-fill frame (<40%), plus the
 *  3 lowest regardless, capped at 8 — incidents get selection priority over
 *  calm frames. Shared by the day racks card and the week grid's fullscreen. */
export function buildRackStrip(photos: LayoutDetailPhoto[]): DayStripPhoto[] {
  const ranked = [...photos]
    .filter((p) => p.image_url && p.fill_pct != null)
    .sort((a, b) => (a.fill_pct as number) - (b.fill_pct as number));
  const dipAts = new Set(
    ranked.filter((p, i) => i < 3 || (p.fill_pct as number) < 40).slice(0, 8).map((p) => p.at),
  );
  return photos.map((p) => ({
    image_url: p.image_url,
    frameUrl: p.frame_image_url ?? null,
    at: p.at,
    metric: p.fill_pct != null ? `${p.fill_pct}%` : null,
    dip: dipAts.has(p.at),
    value: p.fill_pct ?? undefined,
  }));
}

// Fixed image height; each cell's width follows the rack's shared aspect
// (object-contain) so neighbouring frames never touch — a small gap separates
// every slot.
const IMG_H = 84; // px — matches the "kadры" miniature feel
const DEFAULT_SLOT_W = 96; // px — nominal placeholder width (pre-load) + fit-count unit
const GAP = 6; // px — minimal horizontal gap between slots
const ROW_GAP = 6; // px — vertical gap between lanes (fullscreen allows 2)
const RAIL_H = 8; // px — the dot rail's own height
// NOMINAL air between the photo lanes and the rail — room for the ladder of
// distinct elbow heights that keeps OVERLAPPING connectors from crossing (see
// `elbowYs`). The ACTUAL gap used is `railGap` (below), scaled to the rendered
// photo height. A fixed 40 (then 22) read as too much dead air around the rail
// in the small-photo CARD (owner: "лишний отступ ... вокруг скроллбара", card
// AND fullscreen) yet the ladder needs that room where photos — and thus
// connector clusters — are TALL (fullscreen). Scaling by height gives a tight
// rail in the card and keeps ladder room in fullscreen. This const stays as the
// nominal for STRIP_FOOTER_H (the lightbox's pre-measure reservation fallback).
const RAIL_GAP = 22;
// Slim custom scroll indicator drawn ABOVE the photos when the canvas scrolls
// (the native bar rendered mid-strip, right across the connector lines).
const SCROLL_IND_H = 6;
// Total footer height (ladder zone + rail) below the photo lanes — exported
// so callers reserving space for the strip (e.g. the fullscreen lightbox's
// loading placeholder) don't hardcode a stale duplicate of this number.
export const STRIP_FOOTER_H = RAIL_GAP + RAIL_H;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// ── Representative-segment down-selection (coverageFirst strips) ──
// Pick `capacity` frames from `items` (chronological, each carrying a numeric
// `value` = rack fill %) so the strip READS as a summary of the day: the
// endpoints (open/close), a bounded set of photos for each SIGNIFICANT low-fill
// problem, and an even TIME spread over the calm rest so no stretch is empty.
// Supersedes both the old incident-first order (opening-ramp dips ate the whole
// budget → empty afternoon) and the plain even spread (missed real problems).
const RS_PROBLEM_PCT = 40; // fill % below this = a "problem" (empty shelf)
const RS_MIN_SIGNIF_MIN = 25; // ignore micro/quick-fixed dips shorter than this
const RS_MAX_PER_PROBLEM = 3; // photos per problem run (start/mid/end)…
const RS_ALLDAY_FRAC = 0.5; // …unless one run spans >½ the day (then it may take more)
const RS_MIN_COVERAGE_FRAC = 0.33; // always reserve ≥⅓ of the interior slots for calm coverage
function pickRepresentative<T extends { value?: number }>(
  items: { p: T; t: number }[],
  capacity: number,
): number[] {
  const n = items.length;
  const all = () => items.map((_, i) => i);
  if (n <= capacity) return all();
  const t0 = items[0].t;
  const t1 = items[n - 1].t;
  const spanT = Math.max(t1 - t0, 1);
  const minGap = spanT / capacity / 2; // dedup distance
  const fillOf = (i: number) => items[i].p.value;
  const chosen = new Set<number>([0, n - 1]); // always the endpoints
  const slotsLeft = capacity - 2;
  if (slotsLeft <= 0) return [...chosen].sort((a, b) => a - b);

  // 1. Significant problem runs: maximal contiguous fill < threshold whose
  //    Voronoi-bracketed span (edges = midpoint to each neighbour, so a lone
  //    sub-threshold sample gets ~one sample interval, not zero) ≥ the floor.
  type Run = { start: number; end: number; worst: number; span: number };
  const runs: Run[] = [];
  const isProblem = (i: number) => {
    const v = fillOf(i);
    return v != null && v < RS_PROBLEM_PCT;
  };
  for (let i = 0; i < n; ) {
    if (!isProblem(i)) {
      i += 1;
      continue;
    }
    let j = i;
    while (j + 1 < n && isProblem(j + 1)) j += 1;
    const leftEdge = i > 0 ? (items[i].t + items[i - 1].t) / 2 : items[i].t;
    const rightEdge = j < n - 1 ? (items[j].t + items[j + 1].t) / 2 : items[j].t;
    let worst = i;
    for (let k = i; k <= j; k += 1) if ((fillOf(k) ?? 100) < (fillOf(worst) ?? 100)) worst = k;
    runs.push({ start: i, end: j, worst, span: rightEdge - leftEdge });
    i = j + 1;
  }
  const signif = runs.filter((r) => r.span >= RS_MIN_SIGNIF_MIN * 60000);
  const severity = (r: Run) => r.span * (RS_PROBLEM_PCT - (fillOf(r.worst) ?? 0));

  // 2. Reserve coverage; the rest is the problem budget. R1 (spec): a
  //    significant problem must never be photo-less, so guarantee ≥1 problem
  //    slot even at tiny capacity (coverage then just yields to it).
  const coverageSlots = Math.max(1, Math.round(slotsLeft * RS_MIN_COVERAGE_FRAC));
  let problemSlots = slotsLeft - coverageSlots;
  if (signif.length && problemSlots < 1) problemSlots = 1;

  // 3. Allocate the problem budget.
  const problemPicks: number[] = [];
  if (signif.length && problemSlots > 0) {
    if (signif.length >= problemSlots) {
      // More problems than slots → the worst `problemSlots`, one photo each.
      [...signif]
        .sort((a, b) => severity(b) - severity(a))
        .slice(0, problemSlots)
        .forEach((r) => problemPicks.push(r.worst));
    } else {
      // Fewer problems than slots → several photos each (evenly within the run,
      // capped), a day-spanning run may exceed the per-run cap so it still
      // covers the day.
      const alloc = signif.map((r) => ({ r, k: 1 }));
      const capOf = (r: Run) => (r.span > RS_ALLDAY_FRAC * spanT ? problemSlots : RS_MAX_PER_PROBLEM);
      let extra = problemSlots - signif.length;
      const bySpan = [...alloc].sort((a, b) => b.r.span - a.r.span);
      while (extra > 0) {
        let placed = false;
        for (const a of bySpan) {
          if (extra <= 0) break;
          if (a.k < capOf(a.r)) {
            a.k += 1;
            extra -= 1;
            placed = true;
          }
        }
        if (!placed) break; // every run at its cap → leftover rolls into coverage
      }
      for (const { r, k } of alloc) {
        if (k <= 1) {
          problemPicks.push(r.worst);
          continue;
        }
        const ws = items[r.start].t;
        const we = items[r.end].t;
        const picks = new Set<number>();
        for (let m = 0; m < k; m += 1) {
          const target = ws + ((we - ws) * m) / (k - 1);
          let best = -1;
          let bestD = Infinity;
          for (let idx = r.start; idx <= r.end; idx += 1) {
            if (picks.has(idx)) continue;
            const d = Math.abs(items[idx].t - target);
            if (d < bestD) {
              bestD = d;
              best = idx;
            }
          }
          if (best >= 0) picks.add(best);
        }
        picks.forEach((idx) => problemPicks.push(idx));
      }
    }
  }
  // Worst-first so a tight budget keeps the deepest; dedup vs endpoints/each
  // other by minGap (e.g. an opening-ramp worst that IS the open endpoint).
  problemPicks.sort((a, b) => (fillOf(a) ?? 0) - (fillOf(b) ?? 0));
  for (const idx of problemPicks) {
    if (chosen.size >= capacity) break;
    if (chosen.has(idx)) continue;
    let far = true;
    for (const c of chosen)
      if (Math.abs(items[c].t - items[idx].t) < minGap) {
        far = false;
        break;
      }
    if (far) chosen.add(idx);
  }

  // 4. Coverage: distribute the remaining slots over the inter-photo gaps in
  //    PROPORTION to each gap's length, placed evenly WITHIN each gap (snap to
  //    the nearest free frame). Even density; the big calm stretches get most.
  const remaining = capacity - chosen.size;
  if (remaining > 0) {
    const sc = [...chosen].sort((a, b) => items[a].t - items[b].t);
    const gaps = sc.slice(0, -1).map((a, m) => ({ a, b: sc[m + 1], len: items[sc[m + 1]].t - items[a].t }));
    const totalLen = gaps.reduce((s, g) => s + g.len, 0) || 1;
    const alloc = gaps.map((g) => ({ g, exact: (remaining * g.len) / totalLen, k: 0 }));
    alloc.forEach((a) => {
      a.k = Math.floor(a.exact);
    });
    let used = alloc.reduce((s, a) => s + a.k, 0);
    [...alloc]
      .sort((x, y) => y.exact - y.k - (x.exact - x.k))
      .forEach((a) => {
        if (used < remaining) {
          a.k += 1;
          used += 1;
        }
      });
    for (const { g, k } of alloc) {
      for (let m = 1; m <= k; m += 1) {
        const target = items[g.a].t + ((items[g.b].t - items[g.a].t) * m) / (k + 1);
        let best = -1;
        let bestD = Infinity;
        for (let idx = 0; idx < n; idx += 1) {
          if (chosen.has(idx)) continue;
          const tt = items[idx].t;
          if (tt <= items[g.a].t || tt >= items[g.b].t) continue;
          const d = Math.abs(tt - target);
          if (d < bestD) {
            bestD = d;
            best = idx;
          }
        }
        if (best >= 0) chosen.add(best);
      }
    }
  }
  return [...chosen].sort((a, b) => a - b);
}

// Orthogonal photo→rail connector: straight down from the photo's bottom
// centre, a rounded jog sideways at `elbowY`, straight down to the dot.
// Oblique lines at mixed angles read as clutter; elbows keep every connector
// straight top/bottom with only one diagonal-free jog.
function connectorPath(cx: number, y0: number, tx: number, elbowY: number, railMid: number): string {
  const dx = tx - cx;
  if (Math.abs(dx) < 2) return `M ${cx} ${y0} V ${railMid}`;
  const s = Math.sign(dx);
  const r = Math.min(3, Math.abs(dx) / 2);
  return (
    `M ${cx} ${y0} V ${elbowY - r} Q ${cx} ${elbowY} ${cx + s * r} ${elbowY} ` +
    `H ${tx - s * r} Q ${tx} ${elbowY} ${tx} ${elbowY + r} V ${railMid}`
  );
}

export function DayPhotoStrip({
  photos,
  workStart,
  workEnd,
  onOpen,
  slotW: slotWProp = DEFAULT_SLOT_W,
  imgH: imgHProp = IMG_H,
  lanes = 1,
  axisInset,
  onVisiblePhotos,
  mobileScale = 1,
  minCount = 1,
  coverageFirst = false,
  aspectOverride,
  origGeom,
}: {
  photos: DayStripPhoto[];
  /** ISO of the chart's left edge (working-hours start) — desktop alignment. */
  workStart?: string | null;
  /** ISO of the chart's right edge (working-hours end). */
  workEnd?: string | null;
  /** Open the lightbox at the given index into the RENDERED photo set (the one
   *  reported by onVisiblePhotos), NOT the full with-images list. */
  onOpen: (lightboxIdx: number) => void;
  /** Nominal per-image slot width (narrow rack crops fit more → pass smaller). */
  slotW?: number;
  /** Image height in px (the fullscreen strip passes a larger value). */
  imgH?: number;
  /** Rows of frames (fullscreen passes 2 → twice the photos on screen). */
  lanes?: number;
  /** Pixel insets of the paired chart's PLOT AREA (Y-axis width + margins),
   *  so the strip's time→x mapping lands exactly over the chart's x scale.
   *  Photos may still occupy the full width; only the rail/dots are inset. */
  axisInset?: { left: number; right: number };
  /** Reports the photos ACTUALLY rendered, in display order. The desktop strip
   *  drops some frames to fit the width, so the paired lightbox must be built
   *  from THIS set (not the full with-images list) or it pages through more
   *  photos than are on screen (Alexey 2026-07-21). */
  onVisiblePhotos?: (photos: DayStripPhoto[]) => void;
  /** Multiply slotW/imgH by this on MOBILE only. The card strips scroll
   *  horizontally on phones, so photo width doesn't matter — the CARD passes
   *  >1 to make crops bigger (owner: "too small on mobile"). The fullscreen
   *  lightbox leaves it 1 (it already sizes to fill the screen). */
  mobileScale?: number;
  /** Desktop only: guarantee at least this many photos fit the row by capping
   *  the aspect-derived slot width (wide crops SHRINK to fit). Count wins over
   *  natural aspect (847/848). Never pads beyond the photos that exist. */
  minCount?: number;
  /** Down-selection order when more frames are fetched than fit the row.
   *  false (default, queue strip) — INCIDENT-first: keep every marked frame
   *  (busiest queues, 825b) then even-fill. true (rack «Выкладка» strip) —
   *  COVERAGE-first: even full-day spread as the base, then swap in only the
   *  incidents the grid missed, so a cluster of low-fill OPENING frames can't
   *  crowd the afternoon out of the strip (Alexey zone 19 2026-07-22). */
  coverageFirst?: boolean;
  /** «В оригинальных пропорциях» (Алексей 20.10, AI298): ширина/высота стеллажа как на
   *  кадре камеры. Слоты берут это соотношение вместо измеренного у кропа, а сам кроп
   *  растягивается под него (object-fill) — без запроса на бэк. */
  aspectOverride?: number | null;
  /** «В оригинальных пропорциях» (Алексей 21.10 #1671): геометрия стеллажа на кадре — ячейка
   *  показывает описывающий прямоугольник полигона: кусок кадра или кроп, натянутый на полигон. */
  origGeom?: OrigGeom | null;
}) {
  const ft = useFrameTime();
  // Phones keep the horizontal photo SCROLL (owner's call): the canvas
  // widens to natural spacing for up to 20 photos and swipes; desktop fits
  // the viewport exactly, no scroll.
  const mobile = useMediaQuery('(max-width: 767px)');
  // On mobile the strip scrolls, so enlarge crops by mobileScale (card opt-in;
  // lightbox stays 1). Applied to BOTH the width slot and the height cap so
  // wide crops grow proportionally instead of staying width-pinned small.
  const baseSlotW = mobile ? Math.round(slotWProp * mobileScale) : slotWProp;
  const imgH = mobile ? Math.round(imgHProp * mobileScale) : imgHProp;
  // Bigger photos want more air between them: the FULLSCREEN strip (tall imgH,
  // min-10 layout) looked cramped at the card's 6px gap (Alexey 882). Card
  // heights (≤130) keep GAP; taller fullscreen slots scale up to ~18px.
  const gap = Math.max(GAP, Math.min(18, GAP + Math.round((imgH - 130) * 0.09)));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  // All photos of ONE selected rack come from the SAME crop, so they share one
  // aspect ratio. Measure ONLY the FIRST photo to load, then size EVERY slot to
  // that aspect in a single deterministic step (Alexey 2026-07-22, 823). We do
  // NOT measure photos 2..N and NOT shrink-to-tallest — per-image measurement is
  // exactly what caused the earlier height-jump oscillation (808). `null` until
  // the first image loads: slots use the nominal placeholder width until then.
  const [aspect, setAspect] = useState<number | null>(null);
  // Кроп-режим режется в браузере с полного кадра (как на дашборде смены): бековый
  // rack_image — мелкий perspective_* растянутый до 640 (Алексей 22.10 «шакалится»).
  // Кадр не загрузился → для этого фото откат на сохранённый кроп.
  const [cropFailed, setCropFailed] = useState<Set<number>>(() => new Set());

  // Measure ONLY the width (the container's own CSS width — never affected by
  // anything this component renders), which the time→x layout needs. We do NOT
  // measure or report height: feeding a measured height back to a parent that
  // then sizes a sibling created a measure→setState→re-measure loop that made
  // the fullscreen strip oscillate «туда-сюда» (Alexey 2026-07-21). The strip's
  // height is now purely its content (CSS), reserved up front by the caller.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // The photo layer's OWN scroll position — the rail/dots/connectors live in
  // a separate, non-scrolling layer, so a connector's photo-side endpoint has
  // to be recomputed against this whenever the strip scrolls.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollRafRef = useRef<number | null>(null);
  const onStripScroll = useCallback(() => {
    if (scrollRafRef.current != null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      setScrollLeft(scrollRef.current?.scrollLeft ?? 0);
    });
  }, []);
  useEffect(() => {
    return () => {
      if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);
  // A fresh photo set (rack switch, zoom, refetch) starts scrolled to the
  // start — carrying over a stale scroll offset from the PREVIOUS set read
  // as "half the photos went missing" rather than "back at the beginning".
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    setScrollLeft(0);
    // A new set is a new rack → a new crop → a new aspect: drop the old one so
    // the first freshly-loaded photo re-measures it.
    setAspect(null);
  }, [photos]);

  // Height is the stable dimension (the strip is sized by HEIGHT); the slot
  // WIDTH follows from the rack's shared aspect once its first photo loads.
  // Before that, the nominal placeholder width reserves the row so the chart
  // below doesn't jump. After the first onLoad it's ONE width change for the
  // whole strip, not a per-image reflow — and the HEIGHT never moves.
  const withImages = useMemo(() => photos.filter((p) => p.image_url), [photos]);

  const effAspect = aspectOverride ?? aspect;
  const aspectW = effAspect != null ? Math.max(24, Math.round(imgH * effAspect)) : baseSlotW;
  // MINIMUM COUNT WINS over natural aspect: HORIZONTAL (wide) crops — e.g.
  // «Готовая еда» — make aspectW so wide that only ~3 fit the row (Alexey
  // 847/848). On desktop (fixed viewport, no scroll) cap the width so at least
  // `minCount` slots fit the measured container; wide crops SHRINK to fit rather
  // than dropping the count. Count first, natural size second. Mobile scrolls
  // horizontally, so its count is never viewport-bound — leave those at natural
  // (bigger) size.
  //
  // BUT never pad: if the period has fewer than `minCount` photos, size for
  // what actually exists (bigger), don't shrink to fit an absent 6th (849).
  const effMinCount = Math.min(minCount, withImages.length);
  // `lanes` rows share the width, so N total photos need only ~N/lanes per row
  // (the 2-row fullscreen strip packs ~2× per slot width — see `avgStep`).
  const capW =
    !mobile && width > 0 && effMinCount > 1
      ? Math.max(24, Math.floor((lanes * width) / effMinCount) - gap)
      : Infinity;
  const slotW = Math.max(24, Math.min(aspectW, capW));

  const startMs = workStart ? new Date(workStart).getTime() : NaN;
  const endMs = workEnd ? new Date(workEnd).getTime() : NaN;
  const haveDomain =
    Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs;

  // Chart plot-area mapping: time → x lands where the paired chart draws it.
  // Always against the real (unwidened) viewport — see the `layout` memo.
  const plotL = axisInset?.left ?? 0;

  // Time-aligned layout (all viewports, card AND fullscreen). Frames without
  // an image are skipped (a placeholder wastes a slot).
  //
  // Placement:
  //  - 1 row (cards): JUSTIFIED — packed edge-to-edge with evenly spaced
  //    chronological slots.
  //  - 2 rows (fullscreen): photos sit AS CLOSE TO THEIR POINT as possible
  //    (short, rarely-crossing lines) and may overlap each other up to 70%
  //    pairwise (consecutive centres ≥ 0.3·slot apart, alternating rows;
  //    same-row photos never overlap visually) — this packs noticeably more
  //    photos than rigid bricks. Pooled 1D placement spreads any needed
  //    displacement over the whole overlapping run.
  //  - EITHER mode: when every photo doesn't fit the viewport at a natural
  //    (uncompressed) spacing, the PHOTO CANVAS widens instead of dropping
  //    frames — the strip scrolls horizontally to reveal the rest (this used
  //    to silently crop to as few as 3-4 photos, card AND fullscreen alike).
  //    The canvas is independent of the DOT/rail mapping below, which always
  //    stays pinned to the real, unwidened viewport — see the component's
  //    two-layer render split.
  // Sticky selection (Alexey 2026-08-04): `aspect` resolves ONE render after
  // the first image loads (373), which shifts `slotW` and therefore `capacity`
  // — the SAME class of oscillation already fought elsewhere in this file
  // (808/823), just hitting the SELECTION instead of sizing. Without this,
  // hovering right around when aspect settles reads as "photos jerk once and
  // some vanish": the strip silently re-picks a different (often smaller)
  // `chosen` subset for the SAME photo batch. Freeze `chosen` per `photos`
  // identity (stable — see DayChartLightbox's memoized `visible`) the first
  // time it's computed; later re-runs for the same batch (aspect/slotW/width
  // settling) reuse the frozen picks and only recompute POSITIONS live.
  const chosenRef = useRef<{ photos: DayStripPhoto[] | null; chosen: { p: DayStripPhoto; t: number }[] }>({
    photos: null,
    chosen: [],
  });

  const layout = useMemo(() => {
    if (!haveDomain || width <= 0) return null;
    const sorted = photos
      .filter((p) => p.image_url)
      .map((p) => ({ p, t: new Date(p.at).getTime() }))
      .filter((x) => Number.isFinite(x.t))
      .sort((a, b) => a.t - b.t);
    if (!sorted.length) return null;

    const perLane = Math.max(1, Math.floor((width + gap) / (slotW + gap)));
    const laneCount = sorted.length > perLane ? Math.max(1, Math.min(lanes, 2)) : 1;
    const minSep = laneCount > 1 ? Math.max(Math.ceil(slotW * 0.3), 12) : slotW + gap;
    // Effective average step in 2-row mode: the same-lane constraint makes
    // step pairs sum to ≥ slot+gap, so capacity ≈ 2 photos per slot width.
    const avgStep = laneCount > 1 ? (slotW + gap) / 2 : slotW + gap;
    // Desktop TILES the width — the count varies with how many aspect-sized
    // slots fit, it is not clamped to a magic number. The old hard cap of 20
    // left the right-hand part of a wide screen underfilled (owner: "фото в
    // правой части на широком экране всегда не показываются"). The remaining
    // ceiling is only a sanity bound for very narrow crops. Mobile keeps 20
    // (its canvas scrolls, so more just means more swiping).
    const fitCapacity = Math.max(1, Math.floor((width - slotW) / avgStep) + 1);
    const capacity = mobile ? 20 : Math.max(1, Math.min(32, fitCapacity));

    // Desktop: NO horizontal scroll — the strip fits the viewport and SELECTS
    // which frames to show. Two selection orders:
    //  - INCIDENT-first (default, e.g. the queue strip): endpoints + every
    //    marked frame (dip/group — busiest queues, task 825b) + an even fill.
    //    Right when the "interesting" frames are naturally spread across the day
    //    (rush peaks), which they are for the queue.
    //  - COVERAGE-first (`coverageFirst`, the rack «Выкладка» strip):
    //    representative-segment selection (see `pickRepresentative`) — endpoints
    //    + a bounded set per SIGNIFICANT low-fill problem + an even spread over
    //    the calm rest. Empty-shelf dips cluster in the opening stocking ramp,
    //    so incident-first let 5-6 low-fill MORNING frames eat the whole budget
    //    and the afternoon showed ZERO photos (Alexey, zone 19 Готовая еда
    //    2026-07-22: 09:00/09:18/09:40/09:51/11:27/20:50 — then a 9-hour gap).
    // Mobile: the canvas WIDENS to natural spacing for the (≤20) chosen
    // photos and scrolls horizontally.
    let layoutWidth = width;
    let chosen = sorted;
    if (sorted.length > capacity) {
      if (chosenRef.current.photos === photos) {
        // Same batch as a previous pick — reuse it verbatim (see the sticky-
        // selection note above `chosenRef`'s declaration) instead of re-picking
        // with the now-different slotW/capacity.
        chosen = chosenRef.current.chosen;
      } else if (coverageFirst) {
        const keepIdx = new Set(pickRepresentative(sorted, capacity));
        chosen = sorted.filter((_, i) => keepIdx.has(i));
      } else {
        // Incident-first: endpoints + every marked frame, then an even fill.
        const t0 = sorted[0].t;
        const t1 = sorted[sorted.length - 1].t;
        const spanT = Math.max(t1 - t0, 1);
        const keep = new Set<number>();
        keep.add(0);
        keep.add(sorted.length - 1);
        for (const i of sorted
          .map((x, i) => ({ x, i }))
          .filter(({ x }) => x.p.dip || x.p.group != null)
          .map(({ i }) => i)) {
          if (keep.size >= capacity) break;
          keep.add(i);
        }
        for (let j = 0; j < capacity && keep.size < capacity; j += 1) {
          const target = t0 + (spanT * j) / Math.max(capacity - 1, 1);
          let best = -1;
          let bestD = Infinity;
          sorted.forEach((x, i) => {
            if (keep.has(i)) return;
            const d = Math.abs(x.t - target);
            if (d < bestD) {
              bestD = d;
              best = i;
            }
          });
          if (best >= 0) keep.add(best);
        }
        chosen = sorted.filter((_, i) => keep.has(i));
      }
    }
    chosenRef.current = { photos, chosen };
    // Widen the canvas (and let the strip scroll) whenever `chosen.length`
    // doesn't actually fit `width` at the CURRENT slotW — not just on
    // mobile. Two things can make the fit tighter than `capacity` assumed:
    // the sticky-selection cache above freezes `chosen`'s ITEM COUNT from
    // an earlier render (often the pre-aspect nominal slotW) while this
    // block recomputes POSITIONS live against the current, often larger,
    // aspect-derived slotW; and pickRepresentative/incident selection can
    // bunch picks unevenly in time, needing more local room than the
    // capacity's uniform-spacing estimate budgeted. Without widening here,
    // the 2-lane pooled placement below clamps every centre into the
    // unwidened [minC, maxC] regardless — when a cluster's own required
    // span exceeds that room, `baseOf`'s clamp range inverts and the
    // per-item render clamp (`left: clamp(…, 0, layoutWidth - slotW)`)
    // squashes the overflow onto the same boundary, rendering two
    // neighbouring same-lane photos on top of each other (Alexey: "фото
    // накладываются друг на друга … когда идут в два ряда"). This mirrors
    // the "EITHER mode" canvas-widening the header comment above already
    // promises but which only `mobile` implemented.
    if (mobile || lanes > 1) {
      layoutWidth = Math.max(width, (chosen.length - 1) * avgStep + slotW);
    }

    // Dots are pinned to the paired chart's REAL x-axis.
    const dotPlotW = Math.max(width - plotL - (axisInset?.right ?? 0), 1);
    const items = chosen.map(({ p, t }) => {
      const frac = clamp((t - startMs) / (endMs - startMs), 0, 1);
      return { p, trueX: plotL + frac * dotPlotW, off: 0 };
    });
    const minC = slotW / 2;
    const maxC = Math.max(layoutWidth - slotW / 2, minC);
    const n = items.length;

    let centres: number[];
    if (lanes <= 1) {
      // CARD (single-lane context): JUSTIFIED fill — N chronological photos
      // spread evenly edge-to-edge (deliberate product choice, min-6 layout).
      // Only the card ever passes lanes=1; it is never zoomed, so even fill is
      // right here. The multi-lane (fullscreen) branch below anchors to points.
      const step = n > 1 ? (maxC - minC) / (n - 1) : 0;
      centres = items.map((it, i) => (n > 1 ? minC + i * step : clamp(it.trueX, minC, maxC)));
    } else {
      // FULLSCREEN (lanes>1): near-point pooled placement with adaptive
      // separations: consecutive centres ≥ minSep apart, and each same-lane
      // pair (2 steps) ≥ a full slot + gap so photos in one row never overlap
      // visually. `trueX` here is the (narrow, viewport-pinned) dot position,
      // used only as the IDEAL target the pooling tries to stay close to — the
      // min-separation constraint is what actually forces the layout to spread
      // across the wider canvas when there isn't room at the ideal positions.
      // This branch runs even when only ONE lane ends up used (laneCount===1,
      // i.e. FEW photos — e.g. zoomed-in): with the single-lane minSep/avgStep
      // (= slot+gap) each photo anchors directly above its point and spreads
      // only to avoid overlap, instead of justify-filling the whole width
      // (the "расползаются" bug — the justified branch above no longer runs
      // for the fullscreen strip).
      let prevSep = slotW + gap; // first step has no same-lane debt
      for (let i = 1; i < n; i += 1) {
        const sep = Math.max(minSep, slotW + gap - prevSep);
        items[i].off = items[i - 1].off + sep;
        prevSep = sep;
      }
      type Cluster = { sum: number; n: number; firstOff: number; lastOff: number; e: number };
      const baseOf = (cl: Cluster) => clamp(cl.sum / cl.n, minC - cl.firstOff, maxC - cl.lastOff);
      const stack: Cluster[] = [];
      for (const it of items) {
        let cl: Cluster = { sum: it.trueX - it.off, n: 1, firstOff: it.off, lastOff: it.off, e: 0 };
        cl.e = baseOf(cl);
        while (stack.length && stack[stack.length - 1].e > cl.e) {
          const prev = stack.pop()!;
          cl = { sum: prev.sum + cl.sum, n: prev.n + cl.n, firstOff: prev.firstOff, lastOff: cl.lastOff, e: 0 };
          cl.e = baseOf(cl);
        }
        stack.push(cl);
      }
      centres = [];
      for (const cl of stack) {
        for (let k = 0; k < cl.n; k += 1) centres.push(cl.e + items[centres.length].off);
      }
    }

    // Lanes are assigned BOTTOM-FIRST (lane 1 = the row nearest the chart):
    // a photo climbs to the top row only when it would overlap the previous
    // bottom-row photo. Sparse stretches therefore sit in one low row right
    // above the chart — the old strict alternation lifted every other photo a
    // full image height "completely unnecessarily" (owner's words).
    let lanesArr: number[];
    if (laneCount === 1) {
      lanesArr = items.map(() => 0);
    } else {
      lanesArr = new Array(n).fill(1);
      const need = slotW + gap - 0.5;
      let lastBottom = -Infinity;
      let lastTop = -Infinity;
      for (let i = 0; i < n; i += 1) {
        const c = centres[i];
        if (c - lastBottom >= need) {
          lanesArr[i] = 1;
          lastBottom = c;
        } else if (c - lastTop >= need) {
          lanesArr[i] = 0;
          lastTop = c;
        } else if (c - lastBottom >= c - lastTop) {
          lanesArr[i] = 1;
          lastBottom = c;
        } else {
          lanesArr[i] = 0;
          lastTop = c;
        }
      }
      // Everything fit the bottom row → render a single row, no empty lane.
      if (!lanesArr.includes(0)) lanesArr = lanesArr.map(() => 0);
    }
    const placed = items.map((it, i) => ({
      p: it.p,
      left: clamp(centres[i] - slotW / 2, 0, Math.max(layoutWidth - slotW, 0)),
      trueX: it.trueX,
      lane: lanesArr[i],
    }));
    const usedLanes = placed.some((x) => x.lane === 1) ? 2 : 1;
    return { placed, usedLanes, layoutWidth, scrollable: layoutWidth > width + 1 };
  }, [photos, haveDomain, width, startMs, endMs, slotW, gap, lanes, plotL, axisInset?.right, mobile, coverageFirst]);

  // The photos ACTUALLY rendered, in display order — desktop drops some to fit
  // (see `layout`), so the paired lightbox is built from THIS set, and onOpen's
  // index points into it. Falls back to withImages for the plain scroll row.
  const visiblePhotos = useMemo<DayStripPhoto[]>(
    () => (layout ? layout.placed.map((x) => x.p) : withImages),
    [layout, withImages],
  );
  const onVisiblePhotosRef = useRef(onVisiblePhotos);
  onVisiblePhotosRef.current = onVisiblePhotos;
  const visibleSig = visiblePhotos.map((p) => p.at).join('|');
  useEffect(() => {
    onVisiblePhotosRef.current?.(visiblePhotos);
    // Keyed on the set signature; the callback + array are read via a ref, so
    // the exhaustive-deps rule (not enabled in this project) isn't a concern.
  }, [visibleSig]);

  if (!withImages.length) return null;

  // FIXED lane height — every photo cell is exactly imgH tall (no measuring, no
  // shrink-to-tallest). Only the slot WIDTH adapts, once, to the rack's shared
  // aspect; the height is known and reserved on the first paint, so nothing
  // reflows VERTICALLY as images load (Alexey 2026-07-22, 808a/808d + 823).
  const effImgH = imgH;
  // Ячейка — по картинке: широкий кроп, ужатый по ширине до slotW, не оставляет серых полей
  // сверху/снизу (Алексей 28.10 #3132: «фикс-сайз рамка… пустой участок сверху»). Высота ДОРОЖКИ
  // не меняется (ничего не прыгает) — ячейка ниже и прижата к низу, к линиям.
  const cellHFor = (w: number) => (effAspect != null ? Math.min(effImgH, Math.max(16, Math.round(w / effAspect))) : effImgH);

  // Rail gap scaled to the rendered photo height (~0.18×), clamped: the small
  // card photos (~72px → ~13px gap) get a rail that hugs them; the tall
  // fullscreen photos (~110-200px → 20-34px) keep enough ladder band to spread
  // overlapping connectors. This replaces a fixed RAIL_GAP that was either too
  // airy for the card or too tight for dense fullscreen.
  const railGap = clamp(Math.round(effImgH * 0.18), 12, 34);

  // Multi-day strips: the corner chip carries the DAY too («пн 14:20») —
  // a bare clock is ambiguous across a week. Same «more than one MSK day»
  // test as the chart axes (timeAxis.spansDays): the old «> 36 h» left a
  // 1–2-day window / a zoom over midnight with bare clocks (#2646).
  const multiDay = haveDomain && spansDays(startMs, endMs);
  const chipTime = (iso: string): string => {
    if (!multiDay) return ft(iso);
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('ru-RU', { weekday: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
  };

  const renderCell = (
    p: DayStripPhoto,
    opts?: { mobile?: boolean; fixedW?: number; openIndex?: number },
  ) => {
    const time = chipTime(p.at);
    const idx = opts?.openIndex ?? -1;
    return (
      <div
        className={cn(
          'group relative overflow-hidden rounded bg-muted',
          opts?.mobile && 'shrink-0',
          // Interesting frames (biggest dips / longest queues) get a marker
          // line. Mobile only — desktop draws its own bars.
          opts?.mobile && p.dip && 'border-b-2 border-orange-500',
        )}
        // Fixed HEIGHT + aspect-derived width: a muted placeholder box sized on
        // the first paint (nominal width until the first image loads, then the
        // rack's shared aspect), so the image fading in never shifts it
        // vertically (Alexey 2026-07-22, 808 + 823).
        style={{ height: cellHFor(opts?.fixedW ?? slotW), marginTop: effImgH - cellHFor(opts?.fixedW ?? slotW), width: opts?.fixedW }}
      >
        {p.image_url ? (
          <button
            type="button"
            onClick={() => idx >= 0 && onOpen(idx)}
            // Без системной синей обводки фокуса после закрытия лайтбокса (Алексей 28.10 #3134).
            className="block h-full w-full outline-none"
            aria-label={time}
          >
            {origGeom && aspectOverride ? (
              <OrigCropView cropUrl={p.image_url} frameUrl={p.frameUrl} bgFrameUrl={p.bgFrameUrl} geom={origGeom} />
            ) : origGeom && p.frameUrl && !cropFailed.has(idx) ? (
              <PolygonCrop
                frameSrc={p.frameUrl}
                polygons={[origGeom.quad]}
                alt=""
                className="block size-full cursor-zoom-in object-contain"
                onUnavailable={() => setCropFailed((s) => new Set(s).add(idx))}
                onCropAspect={(ar) => setAspect((a) => a ?? ar)}
              />
            ) : (
            <img
              // key по режиму: смена режима пересоздаёт <img>, и onLoad снова снимает
              // opacity-0 (иначе React возвращал класс, а событие уже не приходило — фото серели).
              key={aspectOverride ? 'orig' : 'crop'}
              src={p.image_url}
              alt=""
              // Alexey 10.09: «он тормозит весь кадр. Он может загрузить график
              // и скелетон фото полосы, потом как фото приедут, добавить их».
              // The skeleton already exists (the muted box above) and each image
              // fades in on load — what blocked the frame was DECODING, not the
              // layout. These are full 1920x1080 frames at ~426 KB each
              // (measured 10.09, GitLab #370) and six land at once, so without
              // decoding="async" the browser decodes them on the MAIN thread and
              // the whole card — chart included — janks while it does.
              // decoding="async" hands decode to a background thread: the chart
              // stays interactive and photos pop in as they finish.
              // fetchPriority low so the strip never competes with the chart's
              // own requests; loading stays eager because the strip is
              // horizontal and mostly on-screen, and the FIRST image's onLoad is
              // what fixes the shared aspect for every slot.
              loading="eager"
              decoding="async"
              fetchPriority="low"
              // The FIRST image to load fixes the aspect for EVERY slot (rack
              // crops share one ratio); `a ?? …` makes it a single one-way set —
              // later images never re-measure, so the strip can't oscillate.
              // object-contain (natural aspect, NOT a crop): the slot is already
              // sized to this ratio once measured, so it fills with no letterbox.
              onLoad={(e) => {
                const img = e.currentTarget;
                img.classList.remove('opacity-0');
                if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                  setAspect((a) => a ?? img.naturalWidth / img.naturalHeight);
                }
              }}
              className="block size-full cursor-zoom-in object-contain opacity-0 transition-opacity duration-200"
            />
            )}
            {time && <HoverTime time={time} always compact corner="top-left" />}
            {p.metric != null && p.metric !== '' && (
              <span className="pointer-events-none absolute bottom-0 right-0 z-10 rounded-br rounded-tl bg-black/60 px-0.5 py-px text-[9px] font-medium leading-none text-white backdrop-blur-sm">
                {p.metric}
              </span>
            )}
          </button>
        ) : (
          <div className="flex size-full items-center justify-center">
            <ImageOff className="size-4 text-muted-foreground/40" />
          </div>
        )}
      </div>
    );
  };

  // Hovered photo (index into layout.placed) — emphasizes its connector; the
  // connector itself is hoverable too (wide invisible hitbox) and highlights
  // the photo back.
  const [hovered, setHovered] = useState<number | null>(null);

  const usedLanes = layout?.usedLanes ?? 1;
  // When the canvas scrolls, the whole strip shifts down a few px so the
  // custom scroll indicator has a clear line ABOVE the photos.
  const topPad = layout?.scrollable ? SCROLL_IND_H + 2 : 0;
  const lanesH = topPad + usedLanes * effImgH + (usedLanes - 1) * ROW_GAP;
  const railY = lanesH + railGap;

  // Per-photo RENDERED geometry (depends on each image's aspect as it loads),
  // all in CANVAS space (the photo layer's own scrollable coordinate system):
  // the real photo box inside the slot, the connector's anchor RANGE
  // [axLo, axHi] (canvas space) and the photo's real bottom y. TOP-row
  // anchors are further confined to a gap BETWEEN bottom-row photos, so
  // their descending lines thread through instead of vanishing behind a
  // bottom-row photo. A RANGE, not a resolved point: the anchor's final x is
  // only resolved at connector-draw time against the dot's SCREEN-space
  // trueX (see the render split below) — a straight line whenever the dot's
  // true time is currently scrolled into visual alignment under the photo,
  // the nearest edge of the range otherwise. This range itself is scroll-
  // INVARIANT (every photo in the canvas moves by the same scrollLeft, so
  // their positions relative to EACH OTHER — including bottom-row gaps —
  // never change), only the final resolve-against-trueX step needs the live
  // scroll offset.
  const geo = useMemo(() => {
    if (!layout) return [];
    const twoRows = layout.usedLanes === 2;
    // Bottom-row photo x-ranges, in order — a top-row connector must descend
    // through one of the gaps BETWEEN them (or beside them), never behind.
    const bottomBoxes = layout.placed
      .filter((x) => x.lane === 1)
      .map((x) => [x.left, x.left + slotW] as [number, number]);
    return layout.placed.map(({ left, lane }) => {
      // Every slot is slotW × effImgH (slotW = the rack's aspect at effImgH).
      const w = slotW;
      const imgLeft = left;
      const bottom = topPad + lane * (effImgH + ROW_GAP) + effImgH;
      let axLo = imgLeft + 3;
      let axHi = imgLeft + w - 3;
      if (twoRows && lane === 0) {
        // Clip the anchor range to the widest free interval (between
        // bottom-row photos) that intersects this photo's extent.
        let bestLo = axLo;
        let bestHi = axLo; // degenerate fallback
        let cursor = 0;
        for (const [bl, br] of [...bottomBoxes, [layout.layoutWidth, layout.layoutWidth] as [number, number]]) {
          const gLo = Math.max(cursor + 2, axLo);
          const gHi = Math.min(bl - 2, axHi);
          if (gHi - gLo > bestHi - bestLo) {
            bestLo = gLo;
            bestHi = gHi;
          }
          cursor = Math.max(cursor, br);
        }
        if (bestHi > bestLo) {
          axLo = bestLo;
          axHi = bestHi;
        } else {
          axLo = axHi = (axLo + axHi) / 2;
        }
      }
      return { imgLeft, imgW: w, bottom, axLo, axHi };
    });
  }, [layout, slotW, effImgH, topPad]);

  // Orange marker bars, sized to the RENDERED photos (not the slots): one bar
  // per incident group+lane spanning its member photos edge-to-edge, plus a
  // bar exactly under each loose dip photo. `y` hugs the photo's real bottom.
  // Canvas space — lives in the scrollable photo layer, moves with it.
  const markerBars = useMemo(() => {
    if (!layout) return [];
    const bars: Array<{ lane: number; from: number; to: number; y: number }> = [];
    const byGroup = new Map<string, { lane: number; from: number; to: number; y: number }>();
    layout.placed.forEach(({ p, lane }, i) => {
      const g = geo[i];
      if (!g) return;
      const from = g.imgLeft;
      const to = g.imgLeft + g.imgW;
      const y = g.bottom + 1;
      if (p.group != null) {
        const key = `${p.group}|${lane}`;
        const cur = byGroup.get(key);
        if (!cur) byGroup.set(key, { lane, from, to, y });
        else {
          cur.from = Math.min(cur.from, from);
          cur.to = Math.max(cur.to, to);
          cur.y = Math.max(cur.y, y);
        }
      } else if (p.dip) {
        bars.push({ lane, from, to, y });
      }
    });
    return [...bars, ...byGroup.values()];
  }, [layout, geo]);

  // Non-crossing ladder, computed in SCREEN space (the drawn connectors are
  // scroll-compensated, so ordering elbow heights against the un-scrolled
  // canvas positions mismatched what's actually on screen and lines crossed
  // as soon as the strip scrolled). Within each cluster of overlapping
  // connectors, heights are ordered by an explicit pairwise rule instead of
  // a direction heuristic: putting `a`'s elbow ABOVE `b`'s is bad iff `a`'s
  // dot falls inside `b`'s horizontal run (b's elbow would slice a's
  // descending tail) or `b`'s photo anchor falls inside `a`'s run (a's elbow
  // would slice b's upper drop). A greedy top-down pick minimising those
  // violations handles the mixed-direction clusters two-row strips produce
  // (top-row anchors get displaced into bottom-row gaps, so span directions
  // are NOT homogeneous there — the old ladder crossed exactly then).
  const elbowYs = useMemo(() => {
    if (!layout) return [];
    // Ladder band = [top, bottom]. Widen it within the (owner-approved, tight)
    // rail gap by hugging the photos/rail closer — more vertical spread between
    // rungs makes the "ступеньки" bolder, esp. on mobile where the gap (and so
    // the band) is smallest. Was +8/-4 (band = railGap-12); now +4/-2.
    const top = lanesH + 4;
    const bottom = railY - 2;
    const conns = layout.placed.map(({ trueX }, i) => {
      const g = geo[i];
      const lo0 = (g?.axLo ?? trueX) - scrollLeft;
      const hi0 = (g?.axHi ?? trueX) - scrollLeft;
      const ax = clamp(trueX, Math.min(lo0, hi0), Math.max(lo0, hi0));
      return { ax, tx: trueX, lo: Math.min(ax, trueX), hi: Math.max(ax, trueX) };
    });
    const out: number[] = new Array(conns.length).fill(bottom);
    const inside = (x: number, c: { lo: number; hi: number }) => x > c.lo + 0.5 && x < c.hi - 0.5;
    const badAbove = (a: number, b: number) =>
      (inside(conns[a].tx, conns[b]) ? 1 : 0) + (inside(conns[b].ax, conns[a]) ? 1 : 0);
    const flush = (cluster: number[]) => {
      const k = cluster.length;
      if (k <= 1) return; // singleton keeps the bottom rung (shortest jog)
      const remaining = [...cluster];
      const seq: number[] = [];
      while (remaining.length) {
        let bestAt = 0;
        let bestCost = Infinity;
        for (let i = 0; i < remaining.length; i += 1) {
          let cost = 0;
          for (const other of remaining) {
            if (other !== remaining[i]) cost += badAbove(remaining[i], other);
          }
          if (cost < bestCost) {
            bestCost = cost;
            bestAt = i;
          }
        }
        seq.push(remaining.splice(bestAt, 1)[0]);
      }
      seq.forEach((ci, rank) => {
        out[ci] = top + (rank / (k - 1)) * (bottom - top);
      });
    };
    // Connectors whose photo is scrolled off-screen aren't drawn (see the
    // render skip) — keeping them OUT of the clusters leaves the whole band
    // for the handful actually visible, so their rungs stay far apart.
    const order = conns
      .map((_, i) => i)
      .filter((i) => conns[i].hi >= -24 && conns[i].lo <= width + 24)
      .sort((a, b) => conns[a].lo - conns[b].lo);
    let cluster: number[] = [];
    let gEnd = -Infinity;
    for (const i of order) {
      if (cluster.length && conns[i].lo <= gEnd + 4) {
        cluster.push(i);
        gEnd = Math.max(gEnd, conns[i].hi);
      } else {
        flush(cluster);
        cluster = [i];
        gEnd = conns[i].hi;
      }
    }
    flush(cluster);
    return out;
  }, [layout, geo, lanesH, railY, scrollLeft, width]);

  return (
    <div>
      <div ref={containerRef} className="relative" style={{ height: railY + RAIL_H }}>
        {layout ? (
          <>
            {/* FIXED layer (behind, never scrolls): rail + dots pinned to the
                paired chart's real x-axis, and the connectors linking each
                dot down to its (possibly scrolled) photo above. Doesn't
                intercept pointer events itself — only its own hoverable
                connector hitboxes opt back in — so taps/scroll still reach
                the photo layer on top wherever nothing is drawn. */}
            <div className="pointer-events-none absolute inset-x-0 top-0" style={{ height: railY + RAIL_H }}>
              <svg className="absolute inset-0" width={width || '100%'} height={railY + RAIL_H}>
                {layout.placed.map(({ p, trueX }, i) => {
                  const g = geo[i];
                  if (!g) return null;
                  // The photo's SCREEN-space anchor range right now — canvas
                  // range minus however far the photo layer has scrolled. A
                  // photo scrolled well outside the visible strip has no
                  // useful connector to draw (it would just be a long line
                  // to nowhere on screen), so skip it entirely.
                  const screenLo = g.axLo - scrollLeft;
                  const screenHi = g.axHi - scrollLeft;
                  if (screenHi < -24 || screenLo > width + 24) return null;
                  // Straight line whenever the dot's true time is currently
                  // scrolled into visual alignment under the photo; the
                  // nearest edge of the (possibly scrolled) photo otherwise.
                  const screenAx = clamp(trueX, screenLo, screenHi);
                  const hot = hovered === i;
                  const y0 = g.bottom + 4;
                  // Stop at the DOT'S EDGE — running into its centre was
                  // visible through the dot's anti-aliased rim.
                  const dotR = (p.dip ? 2.5 : 2) + (hot ? 1 : 0);
                  const elbowY = elbowYs[i] ?? railY - 6;
                  const d = connectorPath(screenAx, y0, trueX, elbowY, railY + RAIL_H / 2 - dotR);
                  return (
                    <g key={`l-${p.at}-${i}`}>
                      <path
                        d={d}
                        fill="none"
                        stroke={
                          hot
                            ? p.dip
                              ? '#c2410c'
                              : 'var(--color-foreground)'
                            : p.dip
                              ? '#f97316'
                              : 'var(--color-muted-foreground)'
                        }
                        strokeOpacity={hot ? 0.95 : p.dip ? 0.6 : 0.35}
                        strokeWidth={hot ? 2 : 1}
                        pointerEvents="none"
                      />
                      {/* Invisible fat hitbox — the line itself is hoverable
                          (opts back into pointer events despite the layer's
                          own pointer-events-none). */}
                      <path
                        d={d}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={10}
                        pointerEvents="stroke"
                        onMouseEnter={() => setHovered(i)}
                        onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
                      />
                    </g>
                  );
                })}
              </svg>

              {/* Timeline rail (spanning the chart's real plot area) +
                  per-photo dots at TRUE time-x — always visible, regardless
                  of how far the photo layer above has scrolled. */}
              <div
                className="absolute bg-border"
                style={{ left: plotL, right: axisInset?.right ?? 0, top: railY + RAIL_H / 2, height: 1 }}
              />
              {layout.placed.map(({ p, trueX }, i) => {
                // Hover: the dot grows AND takes the connector's hover colour
                // — underline, line and dot read as one element. No ring.
                const hot = hovered === i;
                const r = (p.dip ? 2.5 : 2) + (hot ? 1 : 0);
                return (
                  <div
                    key={`d-${p.at}-${i}`}
                    className="absolute rounded-full"
                    style={{
                      left: trueX - r,
                      top: railY + RAIL_H / 2 - r,
                      width: r * 2,
                      height: r * 2,
                      background: p.dip
                        ? hot
                          ? '#c2410c'
                          : '#f97316'
                        : hot
                          ? 'var(--color-foreground)'
                          : 'var(--color-muted-foreground)',
                      opacity: hot ? 0.95 : p.dip ? 1 : 0.7,
                    }}
                  />
                );
              })}
              {/* Slim scroll indicator ABOVE the photos (the native bar sat
                  mid-strip, right across the connector lines — it's hidden
                  via .scrollbar-none). Passive: swiping the photos scrolls. */}
              {layout.scrollable && (
                <div
                  className="absolute rounded-full bg-muted-foreground/40"
                  style={{
                    top: 1,
                    height: 3,
                    left:
                      (scrollLeft / Math.max(layout.layoutWidth - width, 1)) *
                      (width - Math.max((width / layout.layoutWidth) * width, 28)),
                    width: Math.max((width / layout.layoutWidth) * width, 28),
                  }}
                />
              )}
            </div>

            {/* SCROLLABLE layer (on top): the photo thumbnails only. This is
                what actually swipes — the rail/dots above never move. */}
            <div
              ref={scrollRef}
              onScroll={onStripScroll}
              className="scrollbar-none absolute inset-x-0 top-0 overflow-x-auto overflow-y-hidden"
              style={{ height: lanesH + 5 }}
            >
              <div className="relative" style={{ height: lanesH + 5, width: layout.layoutWidth }}>
                {layout.placed.map(({ p, left, lane }, i) => (
                  <div
                    key={`${p.at}-${i}`}
                    className={cn('absolute', hovered === i && 'z-10')}
                    // effImgH, NOT imgH: the lane pitch must match the one the
                    // container height (`lanesH`) and the connector anchors
                    // (`geo.bottom`) are computed from. With the nominal imgH
                    // here, any photo shorter than nominal pushed the BOTTOM
                    // lane past the (overflow-hidden) canvas — the row vanished
                    // while its connectors still pointed at the empty space
                    // where it should have been (zone 47, and the right-hand
                    // sparse end of every wide strip).
                    style={{ left, top: topPad + lane * (effImgH + ROW_GAP), width: slotW }}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
                  >
                    {renderCell(p, { fixedW: slotW, openIndex: i })}
                  </div>
                ))}

                {/* Orange markers: exactly as wide as the photo (or the
                    incident's photo span), hugging the photo's real bottom
                    edge. */}
                {markerBars.map((b, i) => (
                  <div
                    key={`g-${i}`}
                    className="pointer-events-none absolute bg-orange-500"
                    style={{
                      left: b.from,
                      width: Math.max(b.to - b.from, 8),
                      top: b.y,
                      height: 2,
                    }}
                  />
                ))}

                {/* Hover feedback: underline + line + dot read as ONE element
                    — the underline touches the line's start (bottom+2..+4 vs
                    line y0 = bottom+4) and shares its colour (foreground;
                    darker orange for dips). */}
                {hovered != null && geo[hovered] && (
                  <div
                    className="pointer-events-none absolute"
                    style={{
                      left: geo[hovered].imgLeft,
                      width: geo[hovered].imgW,
                      top: geo[hovered].bottom + 2,
                      height: 2,
                      background: layout.placed[hovered].p.dip ? '#c2410c' : 'var(--color-foreground)',
                      opacity: 0.95,
                    }}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          // No working-hours domain (or width not measured yet) → a plain
          // chronological scroll row.
          <div className="flex gap-2 overflow-x-auto pb-1">
            {withImages.map((p, i) => (
              <div key={`${p.at}-${i}`} className="shrink-0">
                {renderCell(p, { mobile: true, fixedW: slotW, openIndex: i })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Index (into the with-images list) of the photo whose timestamp is nearest
 *  to the given ISO moment. Returns -1 when there are no images. Used to wire
 *  "click a chart point → open the photo for that moment". */
export function nearestPhotoIndex(
  photos: DayStripPhoto[],
  momentIso: string | null | undefined,
): number {
  if (!momentIso) return -1;
  const target = new Date(momentIso).getTime();
  if (!Number.isFinite(target)) return -1;
  const withImages = photos.filter((p) => p.image_url);
  if (!withImages.length) return -1;
  let best = -1;
  let bestDelta = Infinity;
  withImages.forEach((p, i) => {
    const t = new Date(p.at).getTime();
    if (!Number.isFinite(t)) return;
    const d = Math.abs(t - target);
    if (d < bestDelta) {
      bestDelta = d;
      best = i;
    }
  });
  return best;
}

/** Measure a container and report how many images of ~slotW fit its width.
 *  Returns 6 until measured (and on mobile the caller hard-codes 6).
 *
 *  Uses a CALLBACK ref (not a RefObject) on purpose: the measured node is
 *  usually rendered behind a `{!isLoading && data && …}` gate, so on first
 *  mount the node doesn't exist yet. A plain useLayoutEffect would run once
 *  with a null ref, never attach the observer, and leave count pinned at 6
 *  forever. The callback re-runs whenever the node mounts/unmounts. */
export function useFitCount(slotW: number = DEFAULT_SLOT_W): {
  ref: (node: HTMLDivElement | null) => void;
  count: number;
} {
  const [count, setCount] = useState(6);
  const roRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      roRef.current?.disconnect();
      roRef.current = null;
      if (!node) return;
      const measure = (w: number) => {
        if (w <= 0) return;
        // "the more the better" but don't make them too small → ~slotW each
        // (+ the inter-slot gap), capped so the request stays sane.
        const n = Math.max(4, Math.min(Math.floor((w + GAP) / (slotW + GAP)), 48));
        setCount(n);
      };
      const ro = new ResizeObserver((entries) => {
        for (const e of entries) measure(e.contentRect.width);
      });
      ro.observe(node);
      measure(node.getBoundingClientRect().width);
      roRef.current = ro;
    },
    [slotW],
  );
  return { ref, count };
}
